import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';

import { CloseOutlined } from '@mui/icons-material';

import TemplatesPanel from
  '../../../../../Common/components/Template/TemplatesPanel';

import TemplateTextPanel from
  '../../../../../Common/components/Template/TemplateTextPanel';

import CommonDialogActions from
  '../../../../../Common/components/CommonDialogActions';

import {
  dialogHeaderStyles
} from '../../../../../Common/components/Styles/InputStyles';

import {
  dialogContentSx,
  dialogPaperSx,
  panelsWrapperSx
} from './Styles';

const closeIconSx = {
  fontSize: 22
};

const initialTopics = [
  {
    id: 1,
    title: 'Annual Exam'
  },
  {
    id: 2,
    title: 'Back Pain'
  },
  {
    id: 3,
    title: 'Diarrhea'
  },
  {
    id: 4,
    title: 'Knee Pain'
  },
  {
    id: 5,
    title: 'Lab orders'
  },
  {
    id: 6,
    title: 'Nausea Vomiting'
  },
  {
    id: 7,
    title: 'Swallowing Difficulty'
  },
  {
    id: 8,
    title: 'URI'
  },
  {
    id: 9,
    title: 'UTI'
  },
  {
    id: 10,
    title: 'Varicose Veins'
  },
  {
    id: 11,
    title: 'Imaging order'
  }
];

const initialTemplateText = {
  1: [
    {
      id: 1,
      text: 'CBC, CMP, fasting lipid panel',
      checked: true
    },
    {
      id: 2,
      text: 'Hemoglobin A1C',
      checked: true
    }
  ],

  2: [
    {
      id: 1,
      text: 'Lumbar spine X-ray',
      checked: true
    },
    {
      id: 2,
      text: 'CBC and metabolic panel',
      checked: false
    }
  ],

  3: [
    {
      id: 1,
      text: 'Stool culture',
      checked: true
    },
    {
      id: 2,
      text: 'CBC and electrolyte panel',
      checked: true
    }
  ],

  4: [
    {
      id: 1,
      text: 'Knee X-ray',
      checked: true
    },
    {
      id: 2,
      text: 'Uric acid test',
      checked: false
    }
  ],

  5: [
    {
      id: 1,
      text: 'CBC, CMP, fasting lipid panel, hemoglobin A1C',
      checked: true
    },
    {
      id: 2,
      text: 'PSA, free PSA',
      checked: true
    },
    {
      id: 3,
      text: 'TSH, free T4',
      checked: true
    }
  ],

  6: [
    {
      id: 1,
      text: 'CBC and metabolic panel',
      checked: true
    }
  ],

  7: [
    {
      id: 1,
      text: 'Barium swallow study',
      checked: true
    }
  ],

  8: [
    {
      id: 1,
      text: 'CBC',
      checked: true
    },
    {
      id: 2,
      text: 'Throat culture',
      checked: false
    }
  ],

  9: [
    {
      id: 1,
      text: 'Urinalysis',
      checked: true
    },
    {
      id: 2,
      text: 'Urine culture',
      checked: true
    }
  ],

  10: [
    {
      id: 1,
      text: 'Venous Doppler ultrasound',
      checked: true
    }
  ],

  11: [
    {
      id: 1,
      text: 'Chest X-ray',
      checked: true
    },
    {
      id: 2,
      text: 'Abdominal ultrasound',
      checked: false
    }
  ]
};

const AssessmentTemplatesDialog = ({
  open,
  onClose,
  onSave
}) => {
  const [topics, setTopics] = useState(initialTopics);

  // Lab orders is selected initially to match the reference image.
  const [selectedTopicId, setSelectedTopicId] = useState(5);

  const [templateText, setTemplateText] = useState(
    initialTemplateText
  );

  const selectedTopic =
    topics.find(topic => topic.id === selectedTopicId) ||
    topics[0] ||
    null;

  const selectedRows = templateText[selectedTopicId] || [];

  const handleAddTopic = value => {
    const trimmedValue = value.trim();

    if (!trimmedValue) return;

    const nextId =
      topics.length > 0
        ? Math.max(...topics.map(topic => topic.id)) + 1
        : 1;

    const newTopic = {
      id: nextId,
      title: trimmedValue
    };

    setTopics(previousTopics => [
      ...previousTopics,
      newTopic
    ]);

    setTemplateText(previous => ({
      ...previous,
      [nextId]: []
    }));

    setSelectedTopicId(nextId);
  };

  const handleEditTopic = (topicId, updatedTitle) => {
    const trimmedTitle = updatedTitle.trim();

    if (!trimmedTitle) return;

    setTopics(previousTopics =>
      previousTopics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              title: trimmedTitle
            }
          : topic
      )
    );
  };

  const handleAddText = value => {
    const trimmedValue = value.trim();

    if (!trimmedValue || !selectedTopicId) return;

    const currentRows = templateText[selectedTopicId] || [];

    const nextId =
      currentRows.length > 0
        ? Math.max(...currentRows.map(item => item.id)) + 1
        : 1;

    const newRow = {
      id: nextId,
      text: trimmedValue,
      checked: true
    };

    setTemplateText(previous => ({
      ...previous,
      [selectedTopicId]: [
        ...(previous[selectedTopicId] || []),
        newRow
      ]
    }));
  };

  const handleToggleText = itemId => {
  setTemplateText(previous => ({
    ...previous,
    [selectedTopicId]: (
      previous[selectedTopicId] || []
    ).map(item =>
      item.id === itemId
        ? {
            ...item,
            checked: !item.checked
          }
        : item
    )
  }));
};

  const handleEditText = (itemId, updatedText) => {
    const trimmedText = updatedText.trim();

    if (!trimmedText) return;

    setTemplateText(previous => ({
      ...previous,
      [selectedTopicId]: (
        previous[selectedTopicId] || []
      ).map(item =>
        item.id === itemId
          ? {
              ...item,
              text: trimmedText
            }
          : item
      )
    }));
  };

  const handleReorderText = reorderedRows => {
    setTemplateText(previous => ({
      ...previous,
      [selectedTopicId]: reorderedRows
    }));
  };

  const handleSave = () => {
  const checkedRows = templateText[selectedTopicId] || [];

  const selectedText = checkedRows
    .filter(item => item.checked)
    .map(item => item.text)
    .join('\n');

  onSave({
    text: selectedText,
    topics,
    selectedTopicId,
    templateText
  });

  onClose();
};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: dialogPaperSx
        }
      }}
    >
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>
            Assessment & Plan Templates
          </Typography>

          <Typography sx={dialogHeaderStyles.subtitle}>
           Create and manage templates for assessment and care plans.
          </Typography>
        </Box>

        <IconButton
          aria-label="Close assessment plan templates dialog"
          onClick={onClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}
        >
          <CloseOutlined sx={closeIconSx} />
        </IconButton>
      </DialogTitle>

      
      <DialogContent sx={dialogContentSx}>

        
        <Box sx={panelsWrapperSx}>
          <TemplatesPanel
            topics={topics}
            selectedTopicId={selectedTopicId}
            onSelectTopic={setSelectedTopicId}
            onAddTopic={handleAddTopic}
            onUpdateTopic={handleEditTopic}
          />

          <TemplateTextPanel
            title={selectedTopic?.title || 'Lab Orders'}
            rows={selectedRows}
            onAddText={handleAddText}
            onToggleText={handleToggleText}
            onEditText={handleEditText}
            onReorderText={handleReorderText}
            showCheckbox={true}
          />
        </Box>
      </DialogContent>

      <CommonDialogActions
        onCancel={onClose}
        onSubmit={handleSave}
        submitText="Save"
        disabled={false}
      />
    </Dialog>
  );
};

AssessmentTemplatesDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func
};

AssessmentTemplatesDialog.defaultProps = {
  open: false,
  onClose: () => {},
  onSave: () => {}
};

export default AssessmentTemplatesDialog;